<template>
  <FormCard title="Водитель" source="carrier">
    <FormGrid :cols="singleColumn ? 1 : 2">
      <FormField
        label="Страна"
        help="От страны зависят форматы телефона, паспорта и ВУ"
        :invalid="!form.country.valid && form.country.tochted"
        :error="fieldError(form.country)"
      >
        <select
          id="driver-country"
          v-model="form.country.value"
          @blur="form.country.blur"
        >
          <option v-for="item in countries" :key="item.code" :value="item.code">
            {{ item.flag }} {{ item.label }}
          </option>
        </select>
      </FormField>
      <FormField
        label="ФИО"
        help="Фамилия Имя Отчество полностью"
        :invalid="(!form.full_name.valid && form.full_name.tochted) || !!fieldError(form.full_name)"
        :error="fieldError(form.full_name)"
      >
        <input
          id="driver-name"
          v-model.trim="form.full_name.value"
          type="text"
          @blur="form.full_name.blur"
        />
      </FormField>
      <FormField
        label="Дата рождения"
        :invalid="!form.birthday.valid && form.birthday.tochted"
        :error="fieldError(form.birthday)"
      >
        <date-input
          id="driver-birthday"
          v-model="form.birthday.value"
          @value="setBirthdey"
          @blur="form.birthday.blur"
        />
      </FormField>
      <FormField
        label="Серия и номер паспорта"
        :help="passportHint"
        :invalid="!form.passport.valid && form.passport.tochted"
        :error="fieldError(form.passport)"
      >
        <input
          id="driver-passport"
          v-model.trim="form.passport.value"
          type="text"
          @blur="form.passport.blur"
        />
      </FormField>
      <FormField
        label="Кем выдан"
        :invalid="!form.passport_issued.valid && form.passport_issued.tochted"
        :error="fieldError(form.passport_issued)"
      >
        <input
          id="driver-passport-issued"
          v-model.trim="form.passport_issued.value"
          type="text"
          @blur="form.passport_issued.blur"
        />
      </FormField>
      <FormField
        label="Когда выдан паспорт"
        :invalid="!form.passport_date.valid && form.passport_date.tochted"
        :error="fieldError(form.passport_date)"
      >
        <date-input
          id="driver-passport-date"
          v-model="form.passport_date.value"
          @value="setPasspDt"
          @blur="form.passport_date.blur"
        />
      </FormField>
      <FormField
        label="Водительское удостоверение"
        :invalid="(!form.license.valid && form.license.tochted) || !!errors.license"
        :error="errors.license || fieldError(form.license)"
      >
        <input
          id="driver-license"
          v-model.trim="form.license.value"
          type="text"
          :class="{ invalid: errors.license }"
          @blur="form.license.blur"
        />
      </FormField>
      <FormField
        label="Телефон"
        :help="phoneHint"
        :invalid="(!form.phone.valid && form.phone.tochted) || !!errors.phone"
        :error="errors.phone || fieldError(form.phone)"
      >
        <input
          id="driver-phone"
          v-model="form.phone.value"
          type="text"
          :class="{ invalid: errors.phone }"
          @blur="form.phone.blur"
        />
      </FormField>
      <FormField label="Черный список">
        <input
          id="driver-blacklist"
          v-model="form.blackList.value"
          type="checkbox"
          @blur="form.blackList.blur"
        />
      </FormField>
    </FormGrid>

    <div class="form-actions">
      <my-shrunk-button
        @click="sendForm"
        :btnSettings="{shrunk:true,name:''}"
      >Сохранить</my-shrunk-button>
      <my-shrunk-button
        class="cancel-btn bg-denied"
        @click="closeForm"
        :btnSettings="{shrunk:true,name:''}"
      >Отменить</my-shrunk-button>
    </div>
  </FormCard>
</template>


<script>
import { computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useForm, useFormat, touchForm } from '@/hooks/form'
import {
  CIS_COUNTRIES,
  countryHint,
  licenseByCountry,
  passportByCountry,
  phoneByCountry,
} from '@/constants/cisCountries.mjs'


export default {
  name: 'DriverForm',
  props: {
    mode: { type: String, default: 'create' },
    initialData: { type: Object, default: () => ({}) },
    errors: { type: Object, default: null },
    singleColumn: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const store = useStore()
    const errors = computed(() => props.errors || store.state.apiStorage.drivers.formErrors || {})
    const isEdit = computed(() => props.mode === 'edit')
    const countryHolder = { code: 'RU' }
    const form = useForm({
      country: {
        value: 'RU',
        validators: ['required'],
      },
      full_name: {
        value: '',
        validators: ['required', 'fio'],
      },
      birthday: {
        value: '',
        validators: ['required', 'date'],
      },
      passport: {
        value: '',
        validators: ['required', (value) => passportByCountry(value, countryHolder.code)],
      },
      passport_issued: {
        value: '',
        validators: ['required'],
      },
      license: {
        value: '',
        validators: ['required', (value) => licenseByCountry(value, countryHolder.code)],
      },
      phone: {
        value: '',
        validators: ['required', (value) => phoneByCountry(value, countryHolder.code)],
      },
      passport_date: {
        value: '',
        validators: ['required', 'date_past'],
      },
      blackList: {
        value: false,
        validators: [],
      },
    })

    const fillInitialData = (source = {}) => {
      if (!isEdit.value) return
      form.country.value = source.country || 'RU'
      form.full_name.value = source.full_name || [source.last_name, source.first_name, source.surname]
        .filter(Boolean)
        .join(' ')
      form.birthday.value = source.birthday || ''
      form.passport.value = source.passport || ''
      form.passport_issued.value = source.passport_issued || ''
      form.passport_date.value = source.passport_date || ''
      form.license.value = source.license || ''
      form.phone.value = source.phone || ''
      form.blackList.value = Boolean(source.blackList ?? source.black_list ?? false)
      countryHolder.code = form.country.value
    }
    watch(() => props.initialData, fillInitialData, { immediate: true })

    const fieldError = (field) => field.tochted
      ? Object.values(field.errors).find(Boolean) || ''
      : ''
    const phoneHint = computed(() => countryHint(form.country.value))
    const passportHint = computed(() => {
      const result = passportByCountry('__invalid__', form.country.value)
      return typeof result === 'string' ? result : ''
    })

    watch(() => form.country.value, (code) => {
      countryHolder.code = code
      form.passport.reasign(form.passport.value)
      form.license.reasign(form.license.value)
      form.phone.reasign(form.phone.value)
    })

    const setBirthdey = (newValue) => {
      form.birthday.value = newValue
    }

    const setPasspDt = (newValue) => {
      form.passport_date.value = newValue
    }

    const sendForm = async () => {
      if (await touchForm(form)) {
        const payload = useFormat(form)
        delete payload.blackList
        if (isEdit.value) {
          payload.id = props.initialData.id
          emit('update', payload)
        } else {
          emit('sendForm', { model: 'drivers', form: payload })
        }
      }
    }

    const closeForm = () => {
      emit('closeForm')
    }

    return {
      setPasspDt,
      closeForm,
      setBirthdey,
      form,
      errors,
      fieldError,
      sendForm,
      countries: CIS_COUNTRIES,
      phoneHint,
      passportHint,
    }
  },
}
</script>


<style scoped>
.form-actions {
  display: flex;
  gap: 20px;
  margin-top: 12px;
}

.cancel-btn {
  margin-left: 0;
}
</style>
